"use client";
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FullScreenLoader from "../components/FullScreenLoader";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { motion } from "framer-motion";
import { CheckIcon } from "@heroicons/react/24/solid";
import Alert from "../components/Alert";

import CryptoJS from "crypto-js";
const REGISTER_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/register"; // <-- change to your API

const CLIENT_SHARED_SECRET = process.env?.REACT_APP_JWT_SECRET || "122Codeman!";

export default function AdvisorRegister() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const plan = state?.plan;

  const [alert, setAlert] = useState({
    open: false,
    variant: "info",
    title: "",
    message: "",
    primaryAction: null,
    secondaryAction: null,
  });

  // form state
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // ui state
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  const handleRegister = async () => {
    setError("");

    // quick client-side checks
    if (!fullName || !phone || !email || !password || !confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      const clearBody = {
        fullName: fullName.trim(),
        phone: phone.trim(),
        email: email,
        password: CryptoJS.AES.encrypt(
          password.trim(),
          CLIENT_SHARED_SECRET
        ).toString(), // still hash on server after decrypt
      };

      //console.log(clearBody);
      const res = await fetch(REGISTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(clearBody),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success) {
        setOpen(true);
        localStorage.setItem("bv.registerResponse", JSON.stringify(data));
      } else {
        setAlert({
          open: true,
          variant: "error",
          title: "Registration failed",
          message: "Email already in use. Please try a different one.",
        });
      }
    } catch (e) {
      setError(e.message || "Failed to register.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <FullScreenLoader />}

      <Alert
        open={alert.open}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
        variant={alert.variant}
        title={alert.title}
        message={alert.message}
        primaryAction={alert.primaryAction}
        secondaryAction={alert.secondaryAction}
        autoCloseMs={alert.autoCloseMs}
      />

      <Dialog
        open={open}
        onClose={setOpen}
        className="relative z-10"
        style={{ zIndex: 99 }}
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-sm sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div>
                <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary">
                  <CheckIcon aria-hidden="true" className="size-6 text-white" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold text-gray-900"
                  >
                    Registration successful
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      We’re excited to have you as part of our team! Browse
                      packages, get assessments, and track everything in one
                      place.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigate("/dashboard/assessments/addAssessments");
                    }}
                    className={`w-full mt-4 px-4 py-2 text-sm font-semibold text-white bg-primary`}
                  >
                    Add Assessments
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      <div className="bg-white text-white flex w-full gap-4 direction-row h-[100%]">
        {/* LEFT: Enter information */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-[25%] bg-white flex gap-2 flex-col h-full"
        >
          <div className="border-2 flex flex-col border-primary1 p-2 h-[70%]">
            <h3 className="text-center text-customBlack bg-primary1 text-white p-2">
              ENTER INFORMTION
            </h3>

            <div className="flex flex-col space-y-1 mt-2">
              <label className="text-sm font-medium text-primary">
                Full Name
              </label>
              <input
                className="border border-primary1 text-primary1 px-1 py-1"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe"
                autoComplete="name"
              />
            </div>

            <div className="flex flex-col space-y-1">
              <label className="text-xs font-medium text-primary">
                Phone Number
              </label>
              <input
                className="border border-primary1 text-primary1 px-1 py-1"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                autoComplete="tel"
              />
            </div>

            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-primary">
                Email Address
              </label>
              <input
                className="border border-primary1 text-primary1 px-1 py-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
                autoComplete="email"
              />
            </div>

            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-primary">
                Password
              </label>
              <input
                type="password"
                className="border border-primary1 text-primary1 px-1 py-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>

            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-primary">
                Confirm Password
              </label>
              <input
                type="password"
                className="border border-primary1 text-primary1 px-1 py-1"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>

            {error && <p className="text-red-600 text-xs mt-2">{error}</p>}

            <button
              onClick={handleRegister}
              className="mt-2 w-full font-bold bg-primary1 border text-sm text-white py-2 px-4 hover:bg-white hover:text-primary1 border-primary1 hover:border"
            >
              Register
            </button>

            <p className="text-center text-customBlack text-xs pt-2">
              Already have an account?{" "}
              <span className="text-center text-primary1 text-xs font-bold cursor-pointer">
                Log in
              </span>
            </p>
          </div>

          {/* Secondary card unchanged */}
          <div className="border-2 flex flex-col border-primary1 p-2 h-[30%]">
            <h3 className="text-center text-customBlack font-bold p-2">
              Forgot Username or Password
            </h3>
            <button className="w-full bg-primary1 font-bold text-sm text-white py-2 px-4 hover:bg-white hover:text-primary1 border border-primary1 hover:border">
              Reset Username
            </button>
            <button className="w-full mt-2 bg-primary1 font-bold text-sm text-white py-2 px-4 hover:bg-white hover:text-primary1 border border-primary1 hover:border">
              Reset Password
            </button>
          </div>
        </motion.div>

        {/* The rest of your layout unchanged */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="w-[25%] bg-white flex gap-2 flex-col h-full"
        >
          {/* ... (no changes) */}
          <div className="w-[100%] bg-white p-2 flex gap-4 flex-col h-[60vh] border-2 border-primary1">
            <h3 className="text-center text-customBlack bg-primary1 text-white p-2">
              {`${plan ? "You have selected" : "Choose an option"}`}
            </h3>
            <img
              alt="Ai AssEss"
              src={
                state?.img ||
                "https://res.cloudinary.com/dcfotquxt/image/upload/v1753626241/Ai-AssEss/BizVantage_mo3xvg.png"
              }
              className="h-auto w-48 mx-auto"
            />
            <div className="bg-gray-400 text-gray-900 text-center p-2">
              {state?.monthly || "Monthly Payment"}
            </div>
            <div className="text-gray-900 text-center p-2">
              {state?.subHeading || "Monthly Payment"}
            </div>
            <div className="bg-gray-400 text-gray-900 text-center p-2">
              {state?.plan == "Enterprize Plan" ? (
                <div>
                  <div className="flex flex-col space-y-1 mt-2">
                    <label className="text-sm font-medium text-primary text-left">
                      Name
                    </label>
                    <input
                      className="border border-primary1 text-primary1 px-1 py-1"
                      placeholder="Jane Doe"
                      autoComplete="name"
                    />
                  </div>
                  <div className="flex flex-col space-y-1 mt-2">
                    <label className="text-sm font-medium text-primary text-left">
                      Email
                    </label>
                    <input
                      className="border border-primary1 text-primary1 px-1 py-1"
                      placeholder="john@gmail.com"
                      autoComplete="email"
                    />
                  </div>
                  <div className="flex flex-col space-y-1 mt-2">
                    <label className="text-sm font-medium text-primary text-left">
                      Phone Number
                    </label>
                    <input
                      className="border border-primary1 text-primary1 px-1 py-1"
                      placeholder="1234567890"
                      autoComplete="phone"
                    />
                  </div>
                </div>
              ) : (
                <p className="text-gray-900">Total due $6000</p>
              )}
              <button className="w-[50%] bg-white font-bold text-sm text-primary1 py-2 px-4 hover:bg-primary1 hover:text-white border-2 mt-2 border-primary1 hover:border">
                Submit
              </button>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5 }}
          className="w-[25%] bg-white flex gap-2 flex-col h-full"
        >
          {/* Payment (left as-is) */}
          <div className="w-[100%] bg-white p-2 flex gap-2 flex-col h-[60vh] border-2 border-primary">
            <h3 className="text-center text-customBlack bg-primary1 text-white p-2">
              Payment
            </h3>
            <div className="text-center w-full block mt-4 mb-4">
              <img
                src="https://www.astroshop.eu/CMS/images/text/category/Kreditkarten_Service_all.jpg"
                className="w-28 m-auto"
                alt="Payment image"
              />
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-xs font-medium text-primary">
                Card Number
              </label>
              <input className="border border-primary1 text-primary1 px-3 py-1" />
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-xs font-medium text-primary">
                Expiration Date
              </label>
              <input
                className="border border-primary1 text-primary1 px-3 py-1"
                placeholder="MM/YYYY"
              />
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-xs font-medium text-primary">CVV</label>
              <input className="border border-primary1 text-primary1 px-3 py-1" />
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-xs font-medium text-primary">
                Card Holders Name
              </label>
              <input className="border border-primary1 text-primary1 px-3 py-1" />
            </div>
            <div className="w-full bg-primary1 border border-primary1 p-2 mt-2 flex direction-row items-center gap-2">
              <p className="p-0 m-0 text-sm font-bold w-[65%]">
                Enter Discount Code:
              </p>
              <p className="p-0 m-0 text-sm font-normal w-[35%]">
                <input className="border border-primary1 text-primary1 px-3 py-1 w-full" />
              </p>
            </div>
            <button className=" font-bold w-full bg-primary1 text-sm text-white border py-2 px-4 hover:bg-white hover:text-primary1 border-primary1 hover:border">
              Pay $ 3000
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 2 }}
          className="w-[25%] bg-white flex gap-2 flex-col h-[100%] justify-between"
        >
          <div className="w-[100%] bg-white p-2 flex gap-2 flex-col h-[60vh] border-2 border-primary">
            <h3 className="text-center text-customBlack bg-primary1 text-white p-2">
              SELECT TO UPGRADE
            </h3>
            <div className="relative">
              <div className="absolute t-0 l-0 w-full h-full bg-[#a1a1a1] opacity-25"></div>
              <button className="font-bold w-full gap-4 flex flex-col py-4 bg-primary1 text-sm text-white border py-2 px-4 hover:bg-white hover:text-primary1 border-primary1 hover:border">
                <p>UPGRADE</p>
                <p className="bg-white text-primary1 py-2 text-lg">
                  5 Assessments
                </p>
              </button>
            </div>
          </div>
          <button
            onClick={() => navigate("/dashboard/addClient")}
            className="place-self-end w-full text-center items-center font-bold bg-primary1 text-sm text-white py-2 px-4 hover:bg-white hover:text-primary1 border-primary1 hover:border"
          >
            SAVE
          </button>
        </motion.div>
      </div>
    </div>
  );
}
